// FILE: components/charts.tsx
// This file contains reusable data visualization components.

import React from 'react';

// --- TYPE DEFINITIONS ---
export type LineChartColor = 'brand-orange' | 'blue' | 'green' | 'red' | 'purple';

export type LineChartProps = {
    data: { label: string; value: number }[];
    className?: string;
    color?: LineChartColor;
};

export type BarChartProps = {
    data: { label: string, value: number }[];
    className?: string;
    color?: LineChartColor;
};

// --- COLOR CONFIGURATION ---
export const lineChartColorConfig: Record<LineChartColor, {
    stroke: string;
    gradientFrom: string;
    gradientTo: string;
}> = {
    'brand-orange': {
        stroke: '#f97316',
        gradientFrom: '#f97316',
        gradientTo: '#fed7aa'
    },
    'blue': {
        stroke: '#3b82f6',
        gradientFrom: '#3b82f6',
        gradientTo: '#dbeafe'
    },
    'green': {
        stroke: '#10b981',
        gradientFrom: '#10b981',
        gradientTo: '#d1fae5'
    },
    'red': {
        stroke: '#ef4444',
        gradientFrom: '#ef4444',
        gradientTo: '#fecaca'
    },
    'purple': {
        stroke: '#8b5cf6',
        gradientFrom: '#8b5cf6',
        gradientTo: '#e9d5ff'
    }
};

// --- LINE CHART ---
export const LineChart: React.FC<LineChartProps> = ({
    data = [], 
    className = '', 
    color = 'brand-orange' 
}) => {
    const { stroke } = lineChartColorConfig[color];
    const width = 500;
    const height = 200;
    const padding = 20;

    if (data.length <= 1) {
        return (
            <div className="w-full h-52 flex items-center justify-center text-text-secondary dark:text-dark-text-secondary">
                Not enough data for chart.
            </div>
        );
    }

    const maxValue = Math.max(...data.map(p => p.value), 0);
    const getX = (index: number) => padding + (index / (data.length - 1)) * (width - padding * 2);
    const getY = (value: number) => height - padding - (maxValue > 0 ? (value / maxValue) * (height - padding * 2) : 0);

    const path = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.value)}`).join(' ');
    
    return (
        <div className={`w-full ${className}`} role="img" aria-label="Line chart">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                {/* Grid lines */}
                {Array.from({ length: 5 }).map((_, i) => {
                    const y = padding + (i / 4) * (height - padding * 2);
                    return (
                        <line
                            key={i}
                            x1={padding}
                            y1={y}
                            x2={width - padding}
                            y2={y}
                            stroke="currentColor"
                            className="text-border-color dark:text-dark-border-color opacity-20"
                            strokeWidth="1"
                        />
                    );
                })}
                
                {/* Axes */}
                <line 
                    x1={padding} 
                    y1={padding} 
                    x2={padding} 
                    y2={height - padding} 
                    stroke="currentColor" 
                    className="text-border-color dark:text-dark-border-color" 
                    strokeWidth="2" 
                />
                <line 
                    x1={padding} 
                    y1={height - padding} 
                    x2={width - padding} 
                    y2={height - padding} 
                    stroke="currentColor" 
                    className="text-border-color dark:text-dark-border-color" 
                    strokeWidth="2" 
                />
                
                {/* Y-axis labels */}
                {Array.from({ length: 5 }).map((_, i) => {
                    const value = (maxValue / 4) * (4 - i);
                    const y = padding + (i / 4) * (height - padding * 2);
                    return (
                        <text 
                            key={i} 
                            x={padding - 8} 
                            y={y} 
                            textAnchor="end" 
                            alignmentBaseline="middle" 
                            className="text-xs fill-current text-text-secondary dark:text-dark-text-secondary"
                        >
                            {value.toFixed(0)}
                        </text>
                    );
                })}
                
                {/* Line path */}
                <path 
                    d={path} 
                    fill="none" 
                    stroke={stroke} 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                />
                
                {/* Data points */}
                {data.map((p, i) => (
                    <g key={i} className="cursor-pointer">
                        <title>{`${p.label}: ${p.value}`}</title>
                        <circle
                            cx={getX(i)}
                            cy={getY(p.value)}
                            r="4"
                            fill="white"
                            stroke={stroke}
                            strokeWidth="2"
                            className="hover:r-6 transition-all"
                        />
                    </g>
                ))}
                
                {/* X-axis labels */}
                {data.map((p, i) => (
                    <text 
                        key={i}
                        x={getX(i)} 
                        y={height - padding + 16} 
                        textAnchor="middle" 
                        className="text-xs fill-current text-text-secondary dark:text-dark-text-secondary"
                    >
                        {p.label.length > 8 ? `${p.label.substring(0, 8)}...` : p.label}
                    </text>
                ))}
            </svg>
        </div>
    );
};

// --- BAR CHART ---
export const BarChart: React.FC<BarChartProps> = ({
    data = [],
    className = '',
    color = 'brand-orange'
}) => {
    const { stroke } = lineChartColorConfig[color];
    const width = 500;
    const height = 300;
    const padding = 40;

    if (data.length === 0) {
        return <div className="w-full h-52 flex items-center justify-center text-text-secondary dark:text-dark-text-secondary">No data available for chart.</div>
    }

    const maxValue = Math.max(...data.map(d => d.value), 0);
    const barWidth = (width - padding * 2) / data.length * 0.8;
    const barSpacing = (width - padding * 2) / data.length * 0.2;

    const getX = (index: number) => padding + index * (barWidth + barSpacing) + barSpacing / 2;
    const getY = (value: number) => height - padding - (maxValue > 0 ? (value / maxValue) * (height - padding * 2) : 0);
    const getBarHeight = (value: number) => maxValue > 0 ? (value / maxValue) * (height - padding * 2) : 0;

    return (
        <div className={`w-full ${className}`} role="img" aria-label="Bar chart">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="currentColor" className="text-border-color dark:text-dark-border-color" strokeWidth="2" />
                <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="currentColor" className="text-border-color dark:text-dark-border-color" strokeWidth="2" />
                {Array.from({ length: 5 }).map((_, i) => {
                    const value = (maxValue / 4) * i;
                    const y = height - padding - (i / 4) * (height - padding * 2);
                    return (
                        <text key={i} x={padding - 8} y={y} textAnchor="end" alignmentBaseline="middle" className="text-xs fill-current text-text-secondary dark:text-dark-text-secondary">
                            {value.toFixed(0)}
                        </text>
                    );
                })}
                {data.map((item, index) => {
                    const x = getX(index);
                    const y = getY(item.value);
                    const barHeight = getBarHeight(item.value);
                    return (
                        <g key={index} className="cursor-pointer">
                            <title>{`${item.label}: ${item.value}`}</title>
                            <rect x={x} y={y} width={barWidth} height={barHeight} fill={stroke} className="opacity-80 hover:opacity-100 transition-opacity" rx="4" />
                            <text x={x + barWidth / 2} y={height - padding + 16} textAnchor="middle" className="text-xs fill-current text-text-secondary dark:text-dark-text-secondary">
                                {item.label.length > 8 ? `${item.label.substring(0, 8)}...` : item.label}
                            </text>
                            <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" className="text-xs font-medium fill-current text-text-primary dark:text-dark-text-primary">
                                {item.value}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// --- AREA CHART ---
export const AreaChart: React.FC<LineChartProps> = ({ data = [], className = '', color = 'brand-orange' }) => {
    const { stroke, gradientFrom, gradientTo } = lineChartColorConfig[color];
    const width = 500;
    const height = 200;
    const padding = 20;

    if (data.length <= 1) {
      return <div className="w-full h-52 flex items-center justify-center text-text-secondary dark:text-dark-text-secondary">Not enough data for chart.</div>
    }

    const maxValue = Math.max(...data.map(p => p.value), 0);
    const getX = (index: number) => padding + (index / (data.length - 1)) * (width - padding * 2);
    const getY = (value: number) => height - padding - (maxValue > 0 ? (value / maxValue) * (height - padding * 2) : 0);

    const path = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.value)}`).join(' ');
    const areaPath = `${path} L ${getX(data.length - 1)} ${height - padding} L ${getX(0)} ${height - padding} Z`;
    
    return (
         <div className={`w-full ${className}`} role="img" aria-label="Area chart">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                 <defs>
                    <linearGradient id={`areaGradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={gradientFrom} />
                        <stop offset="100%" stopColor={gradientTo} />
                    </linearGradient>
                </defs>
                <path d={areaPath} fill={`url(#areaGradient-${color})`} />
                <path d={path} fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                 {data.map((p, i) => (
                     <g key={i} className="cursor-pointer">
                        <title>{`${p.label}: ${p.value}`}</title>
                        <circle
                            cx={getX(i)}
                            cy={getY(p.value)}
                            r="5"
                            fill="white"
                            stroke={stroke}
                            strokeWidth="3"
                        />
                    </g>
                ))}
            </svg>
        </div>
    );
};

// --- SCATTER PLOT ---
export type ScatterPlotProps = {
    data: { x: number, y: number }[];
    className?: string;
    color?: LineChartColor;
};

export const ScatterPlot: React.FC<ScatterPlotProps> = ({ data = [], className = '', color = 'brand-orange' }) => {
    const { stroke } = lineChartColorConfig[color];
    const width = 500;
    const height = 300;
    const padding = 30;

    const maxX = Math.max(...data.map(p => p.x), 0);
    const maxY = Math.max(...data.map(p => p.y), 0);

    const getX = (value: number) => padding + (maxX > 0 ? (value / maxX) * (width - padding * 2) : 0);
    const getY = (value: number) => height - padding - (maxY > 0 ? (value / maxY) * (height - padding * 2) : 0);

    return (
        <div className={`w-full ${className}`} role="img" aria-label="Scatter plot">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                {/* Axes */}
                <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="currentColor" className="text-border-color dark:text-dark-border-color" strokeWidth="2" />
                <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="currentColor" className="text-border-color dark:text-dark-border-color" strokeWidth="2" />
                
                 {/* Y Axis Labels */}
                {Array.from({ length: 5 }).map((_, i) => (
                    <text key={i} x={padding - 8} y={getY((maxY / 4) * i)} textAnchor="end" alignmentBaseline="middle" className="text-xs fill-current text-text-secondary dark:text-dark-text-secondary">
                        {((maxY / 4) * i).toFixed(0)}
                    </text>
                ))}

                 {/* X Axis Labels */}
                {Array.from({ length: 5 }).map((_, i) => (
                    <text key={i} x={getX((maxX / 4) * i)} y={height - padding + 16} textAnchor="middle" className="text-xs fill-current text-text-secondary dark:text-dark-text-secondary">
                        {((maxX / 4) * i).toFixed(0)}
                    </text>
                ))}

                {/* Points */}
                {data.map((p, i) => (
                    <g key={i}>
                        <title>{`(${p.x}, ${p.y})`}</title>
                        <circle cx={getX(p.x)} cy={getY(p.y)} r="5" fill={stroke} className="opacity-70 hover:opacity-100 transition-opacity cursor-pointer" />
                    </g>
                ))}
            </svg>
        </div>
    );
};

// --- PIE & DONUT CHARTS ---
type PieChartItem = { id: string; value: number; label: string; colorClass: string; };
export type PieChartProps = {
    data: PieChartItem[];
    className?: string;
    size?: number;
};
export type DonutChartProps = PieChartProps & {
    strokeWidth?: number;
};

const getPieWedgePath = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
    const start = {
        x: cx + radius * Math.cos(startAngle),
        y: cy + radius * Math.sin(startAngle)
    };
    const end = {
        x: cx + radius * Math.cos(endAngle),
        y: cy + radius * Math.sin(endAngle)
    };
    const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";
    return `M ${start.x},${start.y} A ${radius},${radius} 0 ${largeArcFlag} 1 ${end.x},${end.y} L ${cx},${cy} Z`;
};

export const PieChart: React.FC<PieChartProps> = ({ data = [], className = '', size = 150 }) => {
    const total = data.reduce((acc, item) => acc + item.value, 0);
    let startAngle = -Math.PI / 2;

    return (
         <div className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-4 ${className}`} role="img" aria-label="Pie chart">
            <div className="relative shrink-0" style={{ width: size, height: size }}>
                <svg viewBox={`0 0 ${size} ${size}`}>
                    {data.map(item => {
                        if (item.value === 0) return null;
                        const percentage = total > 0 ? item.value / total : 0;
                        const angle = percentage * 2 * Math.PI;
                        const endAngle = startAngle + angle;
                        const pathData = getPieWedgePath(size / 2, size / 2, size / 2, startAngle, endAngle - 0.0001); // subtract tiny amount to avoid full circle rendering issues
                        startAngle = endAngle;

                        return (
                            <g key={item.id} className="cursor-pointer">
                                <title>{`${item.label}: ${item.value.toLocaleString()}`}</title>
                                <path d={pathData} className={`${item.colorClass.replace('bg-', 'fill-')} transition-opacity hover:opacity-80`} />
                            </g>
                        );
                    })}
                </svg>
            </div>
            <div className="space-y-3">
                {data.map(item => (
                    <div key={item.id} className="flex items-center">
                        <span className={`w-3 h-3 rounded-full mr-3 shrink-0 ${item.colorClass}`}></span>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                            <span className="font-bold text-text-primary dark:text-dark-text-primary">{item.label}:</span>
                            <span className="text-text-secondary dark:text-dark-text-secondary font-medium">{item.value.toLocaleString()} ({~~(total > 0 ? (item.value / total * 100) : 0)}%)</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const DonutChart: React.FC<DonutChartProps> = ({
    data: initialData = [],
    className = '',
    size = 150,
    strokeWidth = 20
}) => {
    const data = initialData instanceof Array ? initialData : [];
    const total = data.reduce((acc, item) => acc + item.value, 0);
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    let accumulatedOffset = 0;

    return (
         <div className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-4 ${className}`} role="img" aria-label="Donut chart">
            <div className="relative shrink-0" style={{ width: size, height: size }}>
                <svg viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
                    {data.map(item => {
                        if (item.value === 0) return null;
                        const percentage = total > 0 ? item.value / total : 0;
                        const dasharray = percentage * circumference;
                        const dashoffset = accumulatedOffset * circumference;
                        accumulatedOffset += percentage;

                        return (
                             <circle
                                key={item.id}
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill="none"
                                strokeWidth={strokeWidth}
                                strokeDasharray={`${dasharray} ${circumference - dasharray}`}
                                strokeDashoffset={-dashoffset}
                                className={item.colorClass.replace('bg-', 'stroke-')}
                            />
                        );
                    })}
                </svg>
                 <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-3xl font-bold text-text-primary dark:text-dark-text-primary">{total.toLocaleString()}</span>
                    <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Total</span>
                </div>
            </div>
            <div className="space-y-3">
                {data.map(item => (
                    <div key={item.id} className="flex items-center">
                        <span className={`w-3 h-3 rounded-full mr-3 shrink-0 ${item.colorClass}`}></span>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                            <span className="font-bold text-text-primary dark:text-dark-text-primary">{item.label}:</span>
                            <span className="text-text-secondary dark:text-dark-text-secondary font-medium">{item.value.toLocaleString()} ({~~(total > 0 ? (item.value / total * 100) : 0)}%)</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};